import { useEffect, useState } from 'react'
import { isDesktop, sistemaOperacional, urlDeDownload } from '../lib/platform.ts'

/**
 * Portao de atualizacao do SHELL na inicializacao (pedido 2026-08-25:
 * "quando fecha e abre, deve verificar a atualizacao e fazer ela antes de
 * entrar").
 *
 * A parte WEB ja entra atualizada sozinha — a janela carrega o app do
 * servidor. Este portao cobre o INSTALADOR: ao abrir, compara a versao do
 * shell com o ultimo instalador publicado; se ha mais novo, cobre a tela e
 * chama `shell_atualizar` (o shell baixa o MSI, roda o msiexec, fecha e
 * religa sozinho). Em shells antigos sem esse comando, oferece o download
 * manual e a opcao de continuar.
 */

type Invoke = (cmd: string, args?: Record<string, unknown>) => Promise<unknown>

function ponte(): Invoke | null {
  const w = window as unknown as { __TAURI_INTERNALS__?: { invoke?: Invoke } }
  return w.__TAURI_INTERNALS__?.invoke ?? null
}

function versaoMaisNova(a: string, b: string): boolean {
  const pa = a.split('.').map(Number)
  const pb = b.split('.').map(Number)
  for (let i = 0; i < 3; i++) {
    if ((pa[i] ?? 0) > (pb[i] ?? 0)) return true
    if ((pa[i] ?? 0) < (pb[i] ?? 0)) return false
  }
  return false
}

type Estado =
  | { fase: 'quieto' }
  | { fase: 'instalando'; versao: string }
  | { fase: 'manual'; versao: string }

const ESTILO_FUNDO: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  zIndex: 99999,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'var(--ob-background, #f5f7fb)',
}

/**
 * Com o Orbind na BANDEJA (SPEC-20260916-2111) a janela fica dias sem
 * reabrir, entao a checagem do instalador deixou de ser so na abertura: repete
 * a cada 30 min e ao voltar a ficar visivel. Achou versao nova, segue o mesmo
 * caminho automatico (o shell baixa, instala e religa); a guarda anti-loop de
 * 10 min por versao continua valendo.
 */
const INTERVALO_SHELL_MS = 30 * 60 * 1000

export function ShellUpdateGate() {
  const [estado, setEstado] = useState<Estado>({ fase: 'quieto' })

  useEffect(() => {
    if (!isDesktop()) return
    let cancelado = false
    let verificando = false

    const verificar = async () => {
      if (verificando) return
      verificando = true
      try {
        await verificarShell()
      } finally {
        verificando = false
      }
    }
    const verificarShell = async () => {
      const invoke = ponte()
      if (!invoke) return
      try {
        const atual = (await invoke('plugin:app|version')) as string
        const res = await fetch('/download/releases', { cache: 'no-store' })
        const dados = (await res.json()) as { releases: { platform: string; version: string }[] }
        /*
          O instalador do SISTEMA em que o shell roda (SPEC-20261016-2136-desktop-para-mac).
          Antes comparava sempre com o do Windows: no Mac ofereceria um MSI.
        */
        const sistema = sistemaOperacional()
        const ultima = dados.releases.find((r) => r.platform === sistema)
        if (cancelado || !ultima || !versaoMaisNova(ultima.version, atual)) return

        // Guarda anti-loop: se o automatico acabou de ser tentado para ESTA
        // versao e o app voltou sem atualizar (msiexec falhou, usuario negou o
        // UAC...), insistir viraria um ciclo fechar-abrir infinito. A segunda
        // vez vai direto para o caminho manual.
        const GUARDA = 'orbind:shell-update-tentado'
        try {
          const tentado = JSON.parse(localStorage.getItem(GUARDA) ?? 'null') as {
            versao: string
            quando: number
          } | null
          if (tentado && tentado.versao === ultima.version && Date.now() - tentado.quando < 10 * 60 * 1000) {
            setEstado({ fase: 'manual', versao: ultima.version })
            return
          }
          localStorage.setItem(GUARDA, JSON.stringify({ versao: ultima.version, quando: Date.now() }))
        } catch {
          // localStorage indisponivel: segue sem o guarda
        }

        // fora do Windows não há reinstalação automática: direto ao download
        if (sistema !== 'windows') {
          setEstado({ fase: 'manual', versao: ultima.version })
          return
        }

        // ha instalador mais novo: tenta o caminho automatico (o shell tem um
        // segundo guarda anti-loop em arquivo — ver atualizacao.rs)
        setEstado({ fase: 'instalando', versao: ultima.version })
        try {
          await invoke('shell_atualizar', { versao: ultima.version })
          // o shell encerra o app; se chegou aqui sem fechar, algo impediu
        } catch {
          // shell antigo (sem o comando) ou download falhou: caminho manual
          if (!cancelado) setEstado({ fase: 'manual', versao: ultima.version })
        }
      } catch {
        // sem rede ou sem lista de releases: nao segura a entrada
      }
    }

    void verificar()
    const timer = setInterval(() => void verificar(), INTERVALO_SHELL_MS)
    const aoMudarVisibilidade = () => {
      if (document.visibilityState === 'visible') void verificar()
    }
    document.addEventListener('visibilitychange', aoMudarVisibilidade)

    return () => {
      cancelado = true
      clearInterval(timer)
      document.removeEventListener('visibilitychange', aoMudarVisibilidade)
    }
  }, [])

  if (estado.fase === 'quieto') return null

  return (
    <div style={ESTILO_FUNDO} role="alertdialog" aria-modal="true" aria-label="Atualização do Orbind">
      <div style={{ textAlign: 'center', maxWidth: 420, padding: 24, fontFamily: 'var(--ob-font-body, sans-serif)' }}>
        <i
          className={estado.fase === 'instalando' ? 'fas fa-arrows-rotate fa-spin' : 'fas fa-download'}
          style={{ fontSize: 28, color: 'var(--ob-primary, #2b5cff)' }}
          aria-hidden
        />
        <h1 style={{ font: '600 18px var(--ob-font-display, sans-serif)', color: 'var(--ob-text-strong, #111)', margin: '14px 0 6px' }}>
          {estado.fase === 'instalando'
            ? `Atualizando o Orbind para a versão ${estado.versao}…`
            : `Há uma versão nova do Orbind (${estado.versao})`}
        </h1>
        <p style={{ margin: 0, fontSize: 13.5, color: 'var(--ob-text-muted, #667)' }}>
          {estado.fase === 'instalando'
            ? 'O aplicativo vai fechar, instalar e abrir de novo sozinho. Se o Windows pedir permissão, confirme.'
            : sistemaOperacional() === 'macos'
              ? 'Baixe a versão nova, abra o arquivo e arraste o Orbind para a pasta Aplicativos, substituindo o atual.'
              : 'Esta versão instalada não consegue se atualizar sozinha — baixe o instalador e execute; depois disso as atualizações passam a ser automáticas.'}
        </p>
        {estado.fase === 'manual' ? (
          <div style={{ marginTop: 16, display: 'flex', gap: 10, justifyContent: 'center' }}>
            <a
              href={urlDeDownload()}
              style={{
                background: 'var(--ob-primary, #2b5cff)',
                color: '#fff',
                borderRadius: 10,
                padding: '9px 16px',
                fontSize: 13.5,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Baixar instalador
            </a>
            <button
              type="button"
              onClick={() => setEstado({ fase: 'quieto' })}
              style={{
                background: 'transparent',
                border: '1px solid var(--ob-border, #d5dae6)',
                color: 'var(--ob-text, #223)',
                borderRadius: 10,
                padding: '9px 16px',
                fontSize: 13.5,
                cursor: 'pointer',
              }}
            >
              Continuar mesmo assim
            </button>
          </div>
        ) : null}
      </div>
    </div>
  )
}
